import React, { useState } from 'react';
import { Mail, Phone, MapPin, MessageCircle, Copy, Check, ExternalLink, ShieldCheck } from 'lucide-react';
import { usePeriod } from './PeriodContext';

function Contact() {
  const { selectedPeriod, periodImages } = usePeriod();
  const [copiedItem, setCopiedItem] = useState(null);

  // Background image berdasarkan periode
  const backgroundImage = periodImages?.backgroundImage 
    ? periodImages.backgroundImage 
    : (selectedPeriod === '2025-2026' ? '/bg.jpg' : '/fkpoo.JPG');

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(id);
    setTimeout(() => setCopiedItem(null), 2500);
  };

  const contacts = [
    {
      id: 'akram',
      role: 'Narahubung 1',
      name: 'Muhammad Akram Fatahillah',
      phone: '0877-3734-2777',
      waNumber: '6287737342777',
      desc: 'Pertanyaan umum seputar organisasi & kolaborasi'
    },
    {
      id: 'diny',
      role: 'Narahubung 2',
      name: 'Diny',
      phone: '0812-2524-9789',
      waNumber: '6281225249789',
      desc: 'Pertanyaan seleksi, kepanitiaan, & administrasi'
    }
  ];

  return (
    <div
      className="min-h-screen bg-cover bg-center relative"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      {/* Overlay with blur and depth gradient */}
      <div className="min-h-screen bg-slate-950/85 backdrop-blur-sm flex flex-col justify-center">
        <main className="flex-grow pt-28 pb-16 text-slate-100 flex flex-col justify-center">
          <div className="container mx-auto px-4 sm:px-6">

            {/* --- JUDUL HALAMAN --- */}
            <div className="text-center mb-14 space-y-4 max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-300 text-xs font-bold uppercase tracking-wider">
                <ShieldCheck className="w-3.5 h-3.5 text-indigo-400" />
                Saluran Resmi Organisasi
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
                Hubungi <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">Kami</span>
              </h1>
              <p className="text-base sm:text-lg text-slate-300/90 leading-relaxed">
                Ingin berkolaborasi, mengundang kegiatan, atau menanyakan info resmi seputar FKPO Kulon Progo? Narahubung kami siap membantu Anda.
              </p>
            </div>

            {/* --- MAIN CONTACT WRAPPER --- */}
            <div className="max-w-4xl mx-auto bg-slate-900/70 border border-slate-800/80 rounded-3xl p-6 sm:p-10 md:p-12 shadow-2xl backdrop-blur-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-80 h-80 bg-indigo-600/10 rounded-full blur-[100px] pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-600/10 rounded-full blur-[100px] pointer-events-none" />

              <div className="grid md:grid-cols-2 gap-10 items-stretch relative z-10">

                {/* --- KOLOM KIRI: ALAMAT & EMAIL --- */}
                <div className="flex flex-col justify-between space-y-8 bg-slate-950/50 p-6 sm:p-8 rounded-2xl border border-slate-800/60">
                  <div className="space-y-6">
                    {/* Email Card */}
                    <div className="group">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 bg-indigo-600/15 rounded-xl text-indigo-400 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                          <Mail className="w-5 h-5" />
                        </div>
                        <div>
                          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Surat Elektronik</span>
                          <h3 className="text-base font-bold text-white">Email Resmi</h3>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900 border border-slate-800">
                        <a 
                          href="mailto:fkpokp2526@gmail.com" 
                          className="text-indigo-300 hover:text-white transition-colors text-sm font-mono truncate"
                        >
                          fkpokp2526@gmail.com
                        </a>
                        <button
                          onClick={() => copyToClipboard('fkpokp2526@gmail.com', 'email')}
                          className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors ml-2"
                          title="Salin email"
                        >
                          {copiedItem === 'email' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Alamat Card */}
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 bg-indigo-600/15 rounded-xl text-indigo-400">
                          <MapPin className="w-5 h-5" />
                        </div>
                        <div>
                          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Sekretariat Naungan</span>
                          <h3 className="text-base font-bold text-white">Balai Dikmen Kulon Progo</h3>
                        </div>
                      </div>
                      <div className="p-3.5 rounded-xl bg-slate-900 border border-slate-800 text-xs sm:text-sm text-slate-300 leading-relaxed">
                        Jl. Bhayangkara, Terbah, Wates, Kec. Wates, Kabupaten Kulon Progo, Daerah Istimewa Yogyakarta 55651
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-800 text-xs text-slate-400">
                    💡 Respon tercepat melalui WhatsApp pada hari kerja (08.00 - 17.00 WIB).
                  </div>
                </div>

                {/* --- KOLOM KANAN: LIST NARAHUBUNG (WHATSAPP) --- */}
                <div className="flex flex-col justify-between space-y-4">
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-emerald-400" />
                      Narahubung Langsung (WhatsApp)
                    </h3>
                    <p className="text-xs text-slate-400 mb-5">
                      Klik narahubung di bawah untuk membuka obrolan instan di WhatsApp.
                    </p>

                    <div className="space-y-4">
                      {contacts.map((contact) => (
                        <div
                          key={contact.id}
                          className="p-4 sm:p-5 rounded-2xl bg-slate-950/60 border border-slate-800 hover:border-emerald-500/40 transition-all duration-300 group"
                        >
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                {contact.role}
                              </span>
                              <h4 className="text-base font-bold text-white mt-1 group-hover:text-emerald-300 transition-colors">
                                {contact.name}
                              </h4>
                            </div>
                            <button
                              onClick={() => copyToClipboard(contact.phone.replace(/[^0-9]/g, ''), contact.id)}
                              className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
                              title="Salin nomor"
                            >
                              {copiedItem === contact.id ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                            </button>
                          </div>

                          <p className="text-xs text-slate-400 mb-4">{contact.desc}</p>

                          <a
                            href={`https://wa.me/${contact.waNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl bg-emerald-600/20 hover:bg-emerald-600 text-emerald-300 hover:text-white border border-emerald-500/30 text-xs font-semibold transition-all shadow-md group/btn"
                          >
                            <Phone className="w-3.5 h-3.5" />
                            <span>Hubungi via WhatsApp ({contact.phone})</span>
                            <ExternalLink className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 transition-transform" />
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
}

export default Contact;
